import { Link } from "react-router-dom";
import { fetchDashboardDataFromSheet } from "../api/sheetsApi";
import { useEffect, useState } from "react";
import {
  CheckCircle2,
  Lightbulb,
  FileText,
  ClipboardCheck,
  CalendarDays,
  SquarePen,
  Download,
  Calendar,
} from "lucide-react";

export default function BestSchool() {
  const [generationData, setGenerationData] = useState([]);
  const [generationTargetData, setGenerationTargetData] = useState([]);
  const [generationTargetTotal, setGenerationTargetTotal] = useState(null);
  const [keyParameters, setKeyParameters] = useState([]);
  const [reviewedData, setReviewedData] = useState([]);
  const [reviewedTotal, setReviewedTotal] = useState(null);
  const [summary, setSummary] = useState({});
  const [currentMonth, setCurrentMonth] = useState("May 2026");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const result = await fetchDashboardDataFromSheet();
      setGenerationData(result.GenerationTimelineData || []);
      setGenerationTargetData(result.IdeasGenerationTargetData || []);
      setGenerationTargetTotal(result.IdeasGenerationTargetTotal || null);
      setKeyParameters(result.KeyParametersModel || []);
      setReviewedData(result.ReviewedAndSelectedData || []);
      setReviewedTotal(result.ReviewedAndSelectedTotal || null);
      setSummary(result.bestSchoolSummary || {});
      setCurrentMonth(result.currentMonth || "May 2026");
    } catch (err) {
      setError(err.message);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate derived statistics from API data
  const calculateStats = () => {
    // Total ideas generated for the 14-parameters breakdown table specifically (independent
    // of the top summary card, which now reads its own direct figure from the sheet).
    const totalIdeasGenerated = keyParameters.reduce((sum, param) => sum + (param.ideas || 0), 0);

    const completedStages = generationData.filter(stage => stage.status === "Completed").length;
    const totalStages = generationData.length;

    // Get target date from generation timeline (latest deadline)
    const targetDateRaw =
      generationData.length > 0
        ? generationData[generationData.length - 1].deadline
        : "September 23, 2026";

    const targetDateParsed = new Date(targetDateRaw);
    const formattedTargetDate = Number.isNaN(targetDateParsed.getTime())
      ? targetDateRaw
      : targetDateParsed
          .toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })
          .replace(",", "");

    return {
      totalIdeasGenerated,
      completedStages,
      totalStages,
      targetDate: formattedTargetDate
    };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="p-2 bg-gray-100 min-h-[100dvh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-2 bg-gray-100 min-h-[100dvh] space-y-6">
        <div className="text-center text-red-600">
          <p>Error loading data: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 bg-gray-100 min-h-[100dvh] space-y-6">
      <div className="flex my-2">
        {/* Left Image */}
        {/* <div className="h-28 w-36 overflow-hidden shadow-lg">
          <img
            src="/SchoolImage.png"
            alt="school"
            className="h-full w-full object-cover"
          />
        </div> */}

        {/* Right Content */}
        <div className="relative flex-1 overflow-hidden rounded-r-2xl bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 p-6 text-white shadow-lg">
          <div className="absolute right-10 top-0 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute bottom-0 left-1/3 h-24 w-24 rounded-full bg-pink-400/20 blur-2xl" />

          <div className="relative z-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="relative z-10">
                <h1 className="text-2xl font-bold">Best School Initiative</h1>
                <p className="mt-1 text-sm text-purple-100">
                  Casagrand International Group of Schools
                  <span className="mx-2">|</span>
                  Academic Year 2025-26
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button className="flex items-center justify-center gap-2 w-38 rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur transition hover:bg-white/20">
                <CalendarDays size={18} />
                <span className="text-base">{currentMonth}</span>
              </button>
              <button data-html2canvas-ignore="true" className="flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur transition hover:bg-white/20">
                <Download size={16} />
                Export
              </button>
              <Link data-html2canvas-ignore="true" to={"/edit/bestSchool"} className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-purple-700 transition hover:bg-purple-50">
  <SquarePen size={16} />
  Edit
</Link>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto space-y-6">
        {/* Top Cards */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-6">
          <div className="rounded-2xl bg-pink-100 p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-pink-500">
                <Lightbulb className="h-5 w-5 text-white" />
              </div>
            </div>
            <h4 className="text-sm font-medium text-gray-600">Ideas Generated</h4>
            <h2 className="mt-1 text-2xl font-bold text-gray-900">
              {summary.ideasGenerated || 0}
              </h2>
          </div>

          <div className="rounded-2xl bg-sky-100 p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
                <FileText className="h-5 w-5 text-white" />
              </div>
            </div>
            <h4 className="text-sm font-medium text-gray-600">Parameters</h4>
            <h2 className="mt-1 text-2xl font-bold text-gray-900">{summary.parameters || 0}</h2>
            <p className="mt-1 text-xs text-gray-500">Key Benchmarks</p>
          </div>

          {/* <div className="rounded-2xl bg-red-100 p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-500">
                <ClipboardCheck className="h-5 w-5 text-white" />
              </div>
            </div>
            <h4 className="text-sm font-medium text-gray-600">Stages Done</h4>
            <h2 className="mt-1 text-2xl font-bold text-gray-900">{stats.completedStages} / {stats.totalStages}</h2>
            <p className="mt-1 text-xs text-gray-500">As of Sep 2026</p>
          </div> */}
             <div className="rounded-2xl bg-red-100 p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-500">
                <ClipboardCheck className="h-5 w-5 text-white" />
              </div>
            </div>
            <h4 className="text-sm font-medium text-gray-600">Idea Reviewed</h4>
            <h2 className="mt-1 text-2xl font-bold text-gray-900">{summary.ideaReviewed || 0}</h2>
          </div>

          <div className="rounded-2xl bg-yellow-100 p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-yellow-500">
                <Lightbulb className="h-5 w-5 text-white" />
              </div>
            </div>
            <h4 className="text-sm font-medium text-gray-600">Ideas Selected</h4>
            <h2 className="mt-1 text-2xl font-bold text-gray-900">
              {summary.ideasSelected || 0}</h2>
          </div>

          <div className="rounded-2xl bg-green-100 p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-500">
                <CheckCircle2 className="h-5 w-5 text-white" />
              </div>
            </div>
            <h4 className="text-sm font-medium text-gray-600">Completion</h4>
            <h2 className="mt-1 text-2xl font-bold text-gray-900">
              {summary.completionPercent || "0%"}</h2>
          </div>

          <div className="rounded-2xl bg-blue-100 p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500">
                <CalendarDays className="h-5 w-5 text-white" />
              </div>
            </div>
            <h4 className="text-sm font-medium text-gray-600">Target Date</h4>
            <h2 className="mt-1 text-2xl font-bold text-gray-900">{stats.targetDate}</h2>
            <p className="mt-1 text-xs text-gray-500">Full Completion</p>
          </div>
        </div>

        {/* Two Tables Grid */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Ideas Generation Target */}
          <div className="rounded-2xl bg-white p-6 shadow-sm flex flex-col">
            <h2 className="mb-6 text-xl font-semibold text-[#3A3561]">
              Ideas Generation Target
            </h2>

            <div className="overflow-x-auto flex-1">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 text-left">
                    <th className="px-2 py-1 font-semibold text-gray-700">Meeting</th>
                    <th className="px-2 py-1 font-semibold text-gray-700">Month</th>
                    <th className="px-2 py-1 text-right font-semibold text-gray-700">Target Fixed</th>
                    <th className="px-2 py-1 text-right font-semibold text-gray-700">Target Achieved</th>
                    <th className="px-2 py-1 text-right font-semibold text-gray-700">% Achieved</th>
                    <th className="px-2 py-1 text-right font-semibold text-gray-700">Overall Target %</th>
                  </tr>
                </thead>
                <tbody>
                  {generationTargetData.map((row) => (
                    <tr key={row.stage} className="border-b transition hover:bg-gray-50">
                      <td className="px-2 py-1 font-medium text-gray-700">{row.stage}</td>
                      <td className="px-2 py-1 text-gray-600">{row.month || "-"}</td>
                      <td className="px-2 py-1 text-right text-gray-800">{row.targetFixed || "-"}</td>
                      <td className="px-2 py-1 text-right text-gray-800">{row.targetAchieved || "-"}</td>
                      <td className="px-2 py-1 text-right text-gray-800">{row.percentAchieved ? `${row.percentAchieved}%` : "-"}</td>
                      <td className="px-2 py-1 text-right font-semibold text-gray-800">{row.overallTargetPercent ? `${row.overallTargetPercent}%` : "-"}</td>
                    </tr>
                  ))}
                </tbody>
                {generationTargetTotal && (
                  <tfoot>
                    <tr className="bg-blue-600 text-white">
                      <td className="px-2 py-1 font-bold" colSpan={2}>TOTAL</td>
                      <td className="px-2 py-1 text-right font-bold">{generationTargetTotal.targetFixed || "-"}</td>
                      <td className="px-2 py-1 text-right font-bold">{generationTargetTotal.targetAchieved || "-"}</td>
                      <td className="px-2 py-1 text-right font-bold">{generationTargetTotal.percentAchieved ? `${generationTargetTotal.percentAchieved}%` : "-"}</td>
                      <td className="px-2 py-1 text-right font-bold">{generationTargetTotal.overallTargetPercent ? `${generationTargetTotal.overallTargetPercent}%` : "-"}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Left Card - Meetings Data */}
          <div className="rounded-2xl bg-white shadow-sm overflow-hidden flex flex-col">
            <div className="p-5">
              <h2 className="text-xl font-semibold text-[#3A3561]">
                Total Ideas selected by Arun Sir
              </h2>

              <table className="mt-5 w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="pb-1">Meeting</th>
                    <th className="pb-1">Ideas Reviewed</th>
                    <th className="pb-1 text-right">Ideas Selected</th>
                    <th className="pb-1 text-right">Selection %</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewedData.map((meeting) => (
                    <tr key={meeting.id}>
                      <td className="py-0.5">{meeting.stage}</td>
                      <td className="py-0.5">{meeting.ideas}</td>
                      <td className="py-0.5 text-right font-medium">{meeting.ideasReviewed}</td>
                      <td className="py-0.5 text-right text-gray-600">{meeting.selectionPercent ? `${meeting.selectionPercent}%` : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Total Row */}
            <div className="bg-blue-500 px-5 py-3 text-white">
              <div className="grid grid-cols-4 items-center font-semibold">
                <span>TOTAL IDEAS SELECTED</span>
                <span>{reviewedTotal?.ideas ?? reviewedData.reduce((sum, m) => sum + parseInt(m.ideas), 0)}</span>
                <span className="text-right">{reviewedTotal?.ideasReviewed ?? reviewedData.reduce((sum, m) => sum + parseInt(m.ideasReviewed), 0)}</span>
                <span className="text-right">{reviewedTotal?.selectionPercent ? `${reviewedTotal.selectionPercent}%` : "-"}</span>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-blue-50 px-5 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-gray-600">
                    TARGET COMPLETION DATE
                  </p>
                  <p className="mt-2 text-sm text-gray-700">
                    Total Ideas collected -
                    <span className="ml-1 font-semibold text-blue-600">
                      {summary.ideasGenerated || 0}
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Calendar size={18} className="text-blue-500" />
                  {stats.targetDate}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
